import React from 'react';

const C = {
  navy:     '#1B3A5C',
  navyDark: '#122840',
  teal:     '#2A9D8F',
  white:    '#FFFFFF',
  offWhite: '#F7F9FC',
  slate:    '#64748B',
  border:   '#E2E8F0',
};

const Navbar = () => (
  <nav style={{
    position: 'sticky', top: 0, zIndex: 100,
    background: C.navyDark, borderBottom: `2px solid ${C.teal}`,
    padding: '0 32px',
  }}>
    <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68 }}>
      <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
        <img src="/logo.png" alt="FloridaSIRS" style={{ height: 44 }} />
        <span style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 700, color: C.white }}>
          FloridaSIRS<span style={{ color: C.teal }}>.org</span>
        </span>
      </a>
      <a href="/app" style={{
        background: C.teal, color: C.white, padding: '10px 22px',
        borderRadius: 4, fontSize: 15, fontWeight: 600, textDecoration: 'none',
      }}>Open App →</a>
    </div>
  </nav>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 48 }}>
    <h2 style={{
      fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 700,
      color: C.navy, marginBottom: 16, paddingBottom: 12,
      borderBottom: `2px solid ${C.teal}`,
    }}>{title}</h2>
    {children}
  </div>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontSize: 16, color: C.slate, lineHeight: 1.8, marginBottom: 14 }}>{children}</p>
);

const Li: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li style={{ fontSize: 16, color: C.slate, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>
);

export default function PrivacyPolicy() {
  const lastUpdated = 'March 28, 2026';

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', 'Helvetica Neue', sans-serif; background: ${C.white}; color: ${C.navy}; }
        ul { padding-left: 24px; }
        a { color: ${C.teal}; }
      `}</style>

      <Navbar />

      {/* Header */}
      <div style={{ background: C.navy, padding: '64px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: C.teal, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>Legal</p>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: C.white, marginBottom: 16 }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }}>Last updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: C.offWhite, padding: '64px 24px 96px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', background: C.white, borderRadius: 8, padding: '56px 64px', border: `1px solid ${C.border}`, boxShadow: '0 2px 16px rgba(27,58,92,0.06)' }}>

          <Section title="About FloridaSIRS.org">
            <P>FloridaSIRS.org ("we," "us," or "our") is a privately operated software tool that helps Florida condominium associations generate HB 913-compliant Structural Integrity Reserve Study (SIRS) baseline funding plans. FloridaSIRS.org is not affiliated with the Florida Department of Business and Professional Regulation (DBPR) or the Division of Condominiums, Timeshares and Mobile Homes.</P>
            <P>This Privacy Policy explains what information we collect, how we use it, and your rights regarding that information. By using FloridaSIRS.org, you agree to the practices described in this policy.</P>
          </Section>

          <Section title="Information We Collect">
            <P><strong>Information you provide directly:</strong></P>
            <ul style={{ marginBottom: 16 }}>
              <Li>SIRS PDF documents you upload for data extraction</Li>
              <Li>Component data you enter or edit manually</Li>
              <Li>Association name, unit count, and other parameters you provide</Li>
              <Li>Email address if you contact us at hello@floridasirs.org</Li>
              <Li>Payment information processed by Stripe (we do not store card details)</Li>
            </ul>
            <P><strong>Information collected automatically:</strong></P>
            <ul style={{ marginBottom: 16 }}>
              <Li>Browser type and version</Li>
              <Li>Pages visited and time spent on each page</Li>
              <Li>Referring URL</Li>
              <Li>IP address (anonymised where possible)</Li>
              <Li>Device type (desktop, mobile, tablet)</Li>
            </ul>
          </Section>

          <Section title="How We Use Your Information">
            <P>We use the information we collect to:</P>
            <ul style={{ marginBottom: 16 }}>
              <Li>Process your SIRS PDF and generate baseline funding plan outputs</Li>
              <Li>Deliver the core features of the FloridaSIRS.org tool</Li>
              <Li>Process payments via Stripe</Li>
              <Li>Respond to support requests sent to hello@floridasirs.org</Li>
              <Li>Improve the accuracy of our AI extraction engine</Li>
              <Li>Understand how users interact with the tool so we can improve it</Li>
              <Li>Send product updates if you have opted in to communications</Li>
            </ul>
            <P>We do not sell your data. We do not use your SIRS data for advertising purposes.</P>
          </Section>

          <Section title="PDF Data and Local Processing">
            <P>For free tier users, SIRS PDF processing occurs in your browser using the Anthropic Claude API. The PDF content is transmitted to the Claude API for extraction and is not stored on FloridaSIRS.org servers beyond what is required to complete the extraction request.</P>
            <P>For Pro tier users, your project data may be stored to enable features such as saving and retrieving your funding plan across sessions. Stored data is encrypted at rest.</P>
            <P>You should always review the extracted component data before generating a funding plan. FloridaSIRS.org is not responsible for compliance decisions made on the basis of incorrectly extracted data.</P>
          </Section>

          <Section title="Third-Party Services">
            <P>FloridaSIRS.org uses the following third-party services:</P>
            <ul style={{ marginBottom: 16 }}>
              <Li><strong>Anthropic Claude API</strong> — processes SIRS PDF content for data extraction. Subject to Anthropic's privacy policy at anthropic.com/privacy.</Li>
              <Li><strong>Stripe</strong> — processes subscription payments. We do not store credit card numbers. Subject to Stripe's privacy policy at stripe.com/privacy.</Li>
              <Li><strong>Vercel</strong> — hosts the FloridaSIRS.org application. Subject to Vercel's privacy policy at vercel.com/legal/privacy-policy.</Li>
            </ul>
            <P>We do not share your personal information with third parties except as required to operate these services or as required by law.</P>
          </Section>

          <Section title="Data Retention">
            <P>Free tier: PDF data transmitted for extraction is not retained after the extraction session ends.</P>
            <P>Pro tier: Project data is retained for as long as your subscription is active, plus 90 days after cancellation to allow data export.</P>
            <P>Email correspondence is retained for up to 2 years.</P>
            <P>Note that Florida law (HB 913, §718.112) requires associations to retain SIRS records for at least 15 years. It is your association's responsibility to maintain compliant records independently of FloridaSIRS.org.</P>
          </Section>

          <Section title="Cookies">
            <P>FloridaSIRS.org uses minimal cookies necessary to operate the tool. These include session cookies required for authentication and preference cookies that remember your tool settings. We do not use advertising or tracking cookies.</P>
            <P>You can disable cookies in your browser settings. Disabling cookies may affect the functionality of the tool.</P>
          </Section>

          <Section title="Your Rights">
            <P>You have the right to:</P>
            <ul style={{ marginBottom: 16 }}>
              <Li>Request access to the personal data we hold about you</Li>
              <Li>Request correction of inaccurate personal data</Li>
              <Li>Request deletion of your personal data</Li>
              <Li>Opt out of marketing communications at any time</Li>
              <Li>Export your project data before cancelling your subscription</Li>
            </ul>
            <P>To exercise any of these rights, email <a href="mailto:hello@floridasirs.org">hello@floridasirs.org</a>. We will respond within 30 days.</P>
          </Section>

          <Section title="Security">
            <P>We implement reasonable technical and organisational measures to protect your information. However, no internet transmission is completely secure. You use FloridaSIRS.org at your own risk and should not upload documents containing sensitive personal information beyond what is necessary to generate your funding plan.</P>
          </Section>

          <Section title="Children">
            <P>FloridaSIRS.org is intended for use by adults acting in a professional or fiduciary capacity. We do not knowingly collect personal information from anyone under the age of 18.</P>
          </Section>

          <Section title="Changes to This Policy">
            <P>We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. We encourage you to review this policy periodically. Continued use of FloridaSIRS.org after any change constitutes your acceptance of the updated policy.</P>
          </Section>

          <Section title="Contact">
            <P>If you have questions about this Privacy Policy or how we handle your data, contact us at:</P>
            <div style={{ background: C.offWhite, borderRadius: 6, padding: '24px 28px', border: `1px solid ${C.border}`, marginTop: 8 }}>
              <p style={{ fontSize: 16, color: C.navy, fontWeight: 600, marginBottom: 4 }}>FloridaSIRS.org</p>
              <p style={{ fontSize: 15, color: C.slate }}>Email: <a href="mailto:hello@floridasirs.org">hello@floridasirs.org</a></p>
            </div>
          </Section>

          {/* Disclaimer */}
          <div style={{ marginTop: 56, padding: '24px 28px', background: '#FEF3C7', borderRadius: 6, border: '1px solid #F59E0B' }}>
            <p style={{ fontSize: 14, color: '#92400E', lineHeight: 1.7 }}>
              <strong>Disclaimer:</strong> FloridaSIRS.org generates financial projections based on data from your Structural Integrity Reserve Study. It does not constitute legal, engineering, or financial advice. Consult your licensed professionals for compliance determinations. FloridaSIRS.org is not affiliated with the Florida Department of Business and Professional Regulation (DBPR) or the Division of Condominiums, Timeshares and Mobile Homes.
            </p>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: C.navyDark, padding: '32px 24px', borderTop: `2px solid ${C.teal}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>
            © {new Date().getFullYear()} FloridaSIRS.org. Not affiliated with DBPR. Not legal or financial advice.
          </p>
          <div style={{ display: 'flex', gap: 24 }}>
            <a href="/" style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Home</a>
            <a href="/app" style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Open App</a>
            <a href="mailto:hello@floridasirs.org" style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </footer>
    </>
  );
}
